import type {SingleSelectOptionWithId} from '@github-ui/shared-single-select-components/types'
import {CalendarIcon, MultiSelectIcon, NumberIcon, SingleSelectIcon, TypographyIcon} from '@primer/octicons-react'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {IssueField as IssueFieldPickerIssueField} from '@github-ui/item-picker/IssueFieldPicker'

export const KnownTypes = {
  text: {
    type: 'text' as const,
    displayName: 'Text',
    icon: TypographyIcon,
    valueField: 'textValue' as const,
  },
  single_select: {
    type: 'single_select' as const,
    displayName: 'Single Select',
    icon: SingleSelectIcon,
    valueField: 'singleSelectOptionId' as const,
  },
  multi_select: {
    type: 'multi_select' as const,
    displayName: 'Multi Select',
    icon: MultiSelectIcon,
    valueField: 'multiSelectOptionIds' as const,
  },
  date: {
    type: 'date' as const,
    displayName: 'Date',
    icon: CalendarIcon,
    valueField: 'dateValue' as const,
  },
  number: {
    type: 'number' as const,
    displayName: 'Number',
    icon: NumberIcon,
    valueField: 'numberValue' as const,
  },
} as const

export type IssueFieldType = keyof typeof KnownTypes
export type ValueFields = (typeof KnownTypes)[IssueFieldType]['valueField']

export type KnownType = (typeof KnownTypes)[keyof typeof KnownTypes]

export type IssueFieldSingleSelectOption = {
  id: string
  name: string
  description: string | null | undefined
  color: 'BLUE' | 'GRAY' | 'GREEN' | 'ORANGE' | 'PINK' | 'PURPLE' | 'RED' | 'YELLOW'
}

type IssueFieldSelectionValueTypes = {
  TEXT: string
  SINGLE_SELECT: IssueFieldSingleSelectOption | null
  MULTI_SELECT: IssueFieldSingleSelectOption[] | null
  DATE: Date | null
  NUMBER: number
}

export type IssueFieldSelectionValues = IssueFieldSelectionValueTypes[keyof IssueFieldSelectionValueTypes]

export type FieldVisibilityPermissions = 'org_only' | 'all'

export type IssueField = {
  id: string
  name: string
  description: string
  priority?: number
  type: IssueFieldType
  options?: SingleSelectOptionWithId[]
  visibility?: FieldVisibilityPermissions
}

export type IssueFieldSelection = {
  id: string
  name: string
  dataType: IssueFieldType
}

export const AllKnownTypes = Object.values(KnownTypes)

// Field values we send to mutations (strings, except for number and multi-select fields)
export type FieldValues = Omit<
  Partial<{
    [K in IssueFieldType as (typeof KnownTypes)[K]['valueField']]: string
  }>,
  'numberValue' | 'multiSelectOptionIds'
> & {
  numberValue?: number
  multiSelectOptionIds?: string[]
}

export function getIssueFieldValue(fieldType: IssueFieldType, value?: string): FieldValues {
  switch (fieldType) {
    case 'text':
      return {textValue: value}
    case 'single_select':
      return {singleSelectOptionId: value}
    case 'multi_select':
      if (value === undefined) return {}
      return {multiSelectOptionIds: value === '' ? [] : value.split(',')}
    case 'date':
      return {dateValue: value}
    case 'number': {
      const number = Number(value)
      return isNaN(number) ? {} : {numberValue: number}
    }
  }
}

export function hasMultiSelectOptionIds(value: unknown): value is {multiSelectOptionIds: readonly string[]} {
  return (
    typeof value === 'object' &&
    value !== null &&
    'multiSelectOptionIds' in value &&
    Array.isArray(value.multiSelectOptionIds)
  )
}

// Used to convert from the graphql type that IssueFieldPicker exposes to the shared type
export function asIssueFieldSelection(issueField: IssueFieldPickerIssueField | IssueFieldSelection) {
  return {...issueField, dataType: toIssueFieldType(issueField.dataType)} as IssueFieldSelection
}

// This function converts the issue field type from the graphql type to the shared type
export function toIssueFieldType(fieldType: IssueFieldPickerIssueField['dataType'] | IssueFieldType): IssueFieldType {
  switch (fieldType) {
    case 'TEXT':
      return 'text'
    case 'SINGLE_SELECT':
      return 'single_select'
    case 'MULTI_SELECT':
      return 'multi_select'
    case 'DATE':
      return 'date'
    case 'text':
      return 'text'
    case 'single_select':
      return 'single_select'
    case 'multi_select':
      return 'multi_select'
    case 'date':
      return 'date'
    case 'number':
      return 'number'
    case 'NUMBER':
      return 'number'
    default:
      throw new Error(`Unsupported field type: ${fieldType}`)
  }
}

// relay record type constants for value mutations
export const ISSUE_FIELD_SINGLE_SELECT_VALUE_TYPE = 'IssueFieldSingleSelectValue'
export const ISSUE_FIELD_MULTI_SELECT_VALUE_TYPE = 'IssueFieldMultiSelectValue'
export const ISSUE_FIELD_TEXT_VALUE_TYPE = 'IssueFieldTextValue'
export const ISSUE_FIELD_DATE_VALUE_TYPE = 'IssueFieldDateValue'
export const ISSUE_FIELD_NUMBER_VALUE_TYPE = 'IssueFieldNumberValue'

// a union type for all relay types
export type IssueFieldValueType =
  | typeof ISSUE_FIELD_SINGLE_SELECT_VALUE_TYPE
  | typeof ISSUE_FIELD_MULTI_SELECT_VALUE_TYPE
  | typeof ISSUE_FIELD_TEXT_VALUE_TYPE
  | typeof ISSUE_FIELD_DATE_VALUE_TYPE
  | typeof ISSUE_FIELD_NUMBER_VALUE_TYPE

// determine deletion behavior for mutations
export function shouldRemoveNodeOnDeletion(type: IssueFieldValueType): boolean {
  switch (type) {
    case ISSUE_FIELD_TEXT_VALUE_TYPE:
    case ISSUE_FIELD_NUMBER_VALUE_TYPE:
      return true // remove node entirely
    case ISSUE_FIELD_SINGLE_SELECT_VALUE_TYPE:
    case ISSUE_FIELD_MULTI_SELECT_VALUE_TYPE:
    case ISSUE_FIELD_DATE_VALUE_TYPE:
      return false // clear values but keep node
    default: {
      // cause a TS error if a new type is added but not handled
      const _exhaustiveCheck: never = type
      return _exhaustiveCheck
    }
  }
}

// clear field values based on type for mutations
export function clearFieldValue(
  node: {
    setValue: (value: string | number | null, field: string) => void
    setLinkedRecords?: (records: [], name: string) => void
  } | null,
  type: IssueFieldValueType,
): void {
  if (!node) return

  switch (type) {
    case ISSUE_FIELD_SINGLE_SELECT_VALUE_TYPE:
      node.setValue(null, 'name')
      node.setValue(null, 'color')
      node.setValue(null, 'description')
      break
    case ISSUE_FIELD_MULTI_SELECT_VALUE_TYPE:
      node.setValue(null, 'value') // Value is a comma-separated list of option names.
      if (node.setLinkedRecords) {
        node.setLinkedRecords([], 'options') // Selected options are an array of options.
      }
      break
    case ISSUE_FIELD_DATE_VALUE_TYPE:
    case ISSUE_FIELD_TEXT_VALUE_TYPE:
    case ISSUE_FIELD_NUMBER_VALUE_TYPE:
      node.setValue(null, 'value')
      break
    default: {
      // this will cause a TS error if a new type is added but not handled
      const _exhaustiveCheck: never = type
      return _exhaustiveCheck
    }
  }
}

export function getFieldTypeOcticon(fieldType: IssueField['type']) {
  return KnownTypes[fieldType].icon
}

/**
 * Converts an array of pinned issue field nodes from GraphQL to IssueFieldSelection objects.
 * Filters out null/undefined nodes
 *
 * This is used by both issue metadata and issue creation flows to parse
 * organization-level and issue-type-level pinned fields.
 *
 * @param nodes - Array of field nodes from GraphQL (may contain nulls)
 * @returns Array of IssueFieldSelection objects, or null if no valid fields
 */
export function parsePinnedIssueFields(
  nodes: ReadonlyArray<IssueFieldPickerIssueField | null | undefined> | undefined,
): readonly IssueFieldSelection[] | null {
  if (!nodes || nodes.length === 0) return null

  const filteredFields: IssueFieldSelection[] = []

  for (const field of nodes) {
    if (field && field.id && field.name && field.dataType) {
      filteredFields.push(asIssueFieldSelection(field))
    }
  }

  return filteredFields.length > 0 ? filteredFields : null
}
